import React, { useState, useEffect, useRef } from "react"
import { ORG_DATA, ORG_CATEGORIES } from "../../org/org-const"
import OrgLinkCard from "@/components/org-link-card"
import { orgSearchStore } from "@/routes/select/org/org-search-store" // 스토어 임포트
import Header from "@/routes/_layout/header"
import { langStore } from "@/stores/lang-store"
import useIsMobile from "@/hooks/use-is-mobile"
import SearchFilter from "../_components/search-filter"
import FavoriteButton from "../_components/favorite-button"
import useOutsideClick from "@/hooks/use-outside-click"
import Headline from "@/components/headline"
import Wrapper from "@/components/wrapper"
import { orgSelectBundle } from "./org-select-const"

const OrgSelectPage = () => {
  const isMobile = useIsMobile()
  const mode = isMobile ? "mobile" : "desktop"
  const HeaderComponent = Header.default[mode]

  const { lang } = langStore()

  const { appliedCategory, appliedKeyword, favorites, setSearch, toggleFavorite, resetSearch } =
    orgSearchStore()
  const [isFavOnly, setIsFavOnly] = useState(false)

  useEffect(() => {
    return () => orgSearchStore.getState().resetSearch()
  }, [])

  // 드롭다운 내에서만 쓰이는 로컬 UI 상태 (적용 전 임시값)
  const [isOpen, setIsOpen] = useState(false)
  const [tempCategory, setTempCategory] = useState(appliedCategory || "전체")
  const [tempKeyword, setTempKeyword] = useState(appliedKeyword)
  const filterRef = useRef(null)

  const getCategoryDisplayName = (categoryValue) => {
    if (!categoryValue || categoryValue === "전체") return orgSelectBundle.filter.all[lang]
    return orgSelectBundle.filter.categories[categoryValue]?.[lang] || categoryValue
  }

  useOutsideClick({
    ref: filterRef,
    enabled: isOpen,
    onOutside: () => setIsOpen(false)
  })

  // 검색 조건 존재 확인
  const hasCondition = !!appliedCategory || !!appliedKeyword || isFavOnly

  // 필터링 로직 (Zustand의 상태 기준)
  const filtered = !hasCondition
    ? []
    : ORG_DATA.filter((org) => {
        const matchFav = !isFavOnly || favorites.includes(org.id)
        const matchCategory =
          !appliedCategory || appliedCategory === "전체" || org.category === appliedCategory
        const matchKeyword =
          !appliedKeyword ||
          org?.name[lang].toLowerCase().includes(appliedKeyword) ||
          org?.region[lang].toLowerCase().includes(appliedKeyword)

        return matchFav && matchCategory && matchKeyword
      })

  const handleSearch = () => {
    setIsFavOnly(false)
    setSearch(tempCategory, tempKeyword)
    setIsOpen(false)
  }

  const handleReset = () => {
    resetSearch()
    setTempCategory("전체")
    setTempKeyword("")
    setIsOpen(false)
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <HeaderComponent />

      <Wrapper.maxWidth className="pt-4">
        <Headline
          title={orgSelectBundle.header.title[lang]}
          description={orgSelectBundle.header.subtitle[lang]}
        />

        {/* 검색 및 필터 컨트롤 */}
        <div className="relative mb-4" ref={filterRef}>
          <SearchFilter
            toggleOpen={() => setIsOpen(!isOpen)}
            preview={
              !appliedCategory || appliedCategory === "전체"
                ? orgSelectBundle.filter.categoryPlaceholder[lang]
                : getCategoryDisplayName(appliedCategory)
            }
            placeholder={orgSelectBundle.filter.inputPlaceholder[lang]}
            keyword={tempKeyword}
            setKeyword={setTempKeyword}
            onSearch={handleSearch}
          />

          {/* 1단 드롭다운 */}
          {isOpen && (
            <div className="absolute left-0 w-full md:w-1/3 min-w-35 mt-2 z-50 bg-white border border-slate-200 rounded-2xl shadow-2xl overflow-hidden text-sm flex flex-col top-2/5 md:top-full md:mt-2">
              <div className="p-2 space-y-0.5">
                {/* '전체' 옵션 */}
                <div
                  className={`p-3 rounded-lg cursor-pointer hover:bg-slate-100 ${tempCategory === "전체" ? "bg-blue-50 text-blue-600 font-bold" : ""}`}
                  onClick={() => setTempCategory("전체")}
                >
                  {orgSelectBundle.filter.all[lang]}
                </div>

                {/* ORG_CATEGORIES(국내, 국외) 매핑 */}
                {ORG_CATEGORIES.map((cat) => (
                  <div
                    key={cat}
                    className={`p-3 rounded-lg cursor-pointer hover:bg-slate-100 ${tempCategory === cat ? "bg-blue-50 text-blue-600 font-bold" : ""}`}
                    onClick={() => setTempCategory(cat)}
                  >
                    {getCategoryDisplayName(cat)}
                  </div>
                ))}
              </div>

              {/* 하단 버튼 영역 (기존 코드 유지) */}
              <div className="p-3 bg-slate-50 border-t border-slate-100 flex justify-end gap-2">
                <button
                  onClick={handleReset}
                  className="px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-medium text-slate-600 hover:bg-slate-100"
                >
                  {orgSelectBundle.filter.reset[lang]}
                </button>
                <button
                  onClick={handleSearch}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-bold hover:bg-blue-700"
                >
                  {orgSelectBundle.filter.apply[lang]}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* 즐겨찾기 필터 칩 */}
        <FavoriteButton onClick={() => setIsFavOnly(!isFavOnly)} active={isFavOnly} />

        {/* 결과 리스트 */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6 pb-20">
          {filtered.map((org) => (
            <OrgLinkCard
              key={org.id}
              org={org}
              isFavorite={favorites.includes(org.id)}
              onToggleFavorite={toggleFavorite}
              customUrl={org.path ? `/${org.path}` : undefined}
            />
          ))}

          {!hasCondition && (
            <div className="md:col-span-2 xl:col-span-3 text-center py-20 bg-white rounded-3xl border border-dashed border-slate-200">
              <span className="text-4xl block mb-4">🔍</span>
              <p className="text-slate-500 font-medium text-lg">
                {orgSelectBundle.empty.defaultTitle[lang]}
              </p>
            </div>
          )}

          {hasCondition && filtered.length === 0 && (
            <div className="md:col-span-2 xl:col-span-3 text-center py-20 bg-white rounded-3xl border border-dashed border-slate-200">
              <p className="text-slate-400 font-medium text-lg">
                {orgSelectBundle.empty.noResult[lang]}
              </p>
              <button onClick={handleReset} className="mt-2 text-blue-500 underline text-sm">
                {orgSelectBundle.empty.resetLink[lang]}
              </button>
            </div>
          )}
        </div>
      </Wrapper.maxWidth>
    </div>
  )
}

export default OrgSelectPage
